/**
 * Honest assessment results for a written conversation (R1B.6 product surface).
 *
 * ── THE ONE RULE THIS COMPONENT EXISTS TO ENFORCE ──────────────────────────────
 *
 * A dimension that was not measured is never drawn as a score. No 0%, no empty
 * bar, no red, no "needs improvement". The API sends unavailable dimensions with
 * no numeric field at all, and this component renders them in a visually separate
 * "Not measured" section with the reason in plain language.
 *
 * Likewise there is no overall score unless the server sent one. A missing
 * overall is explained, not rendered as a blank or a zero — because a user who
 * sees "Overall 0%" concludes they failed, and nobody measured that.
 */

import clsx from 'clsx';
import type { ConversationResult, VideoEvidenceValue } from '../store/api/coachingApi';
import { VIDEO_UNAVAILABLE_LABEL } from './video/videoPracticeCopy';

/** Product labels. The API's keys are engineering vocabulary. */
const DIMENSION_LABEL: Record<string, string> = {
  clarity: 'Clarity',
  relevance: 'Relevance',
  completeness: 'Completeness',
  questioning: 'Questioning',
  responsiveness: 'Responsiveness',
  objectionHandling: 'Handling pushback',
  structure: 'Structure',
  confidence: 'Vocal confidence',
  energy: 'Vocal energy',
  pacing: 'Pacing',
  pitch: 'Pitch',
  volume: 'Volume',
  prosody: 'Intonation',
  speechRate: 'Speech rate',
  pauseControl: 'Pause control',
  interruptionTiming: 'Interruption timing',
  gaze: 'Eye contact',
  posture: 'Posture',
  gesture: 'Gesture',
  facialExpression: 'Facial expression',
  visualEngagement: 'Visual engagement',
  // Language practice rubric (P5): judged from the text of the user's turns.
  comprehensibility: 'Comprehensibility',
  grammarAccuracy: 'Grammar accuracy',
  vocabularyRange: 'Vocabulary range',
  taskCompletion: 'Task completion',
  // Acoustic: measured by LevelUp Media, not yet available — never estimated.
  pronunciation: 'Pronunciation',
  spokenFluency: 'Spoken fluency',
};

const fallbackLabel = (key: string) =>
  DIMENSION_LABEL[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

/** Interview practice results are feedback on answers, never a hiring signal. */
export const INTERVIEW_PRACTICE_NOTICE =
  'This is practice feedback on your answers, not a hiring signal. It is not shared with employers, recruiters or anyone else unless you choose to publish it.';

function scoreTone(value: number): string {
  if (value >= 75) return 'bg-green-500';
  if (value >= 50) return 'bg-amber-500';
  return 'bg-red-400';
}

/** Evidence classes in product words (docs/MEDIA-BOUNDARY.md §4). */
const EVIDENCE_CLASS_LABEL: Record<VideoEvidenceValue['evidenceClass'], string> = {
  MEASURED: 'Measured',
  DERIVED: 'Derived',
  UNAVAILABLE: 'Unavailable',
};

function formatVideoValue(v: Extract<VideoEvidenceValue, { state: 'available' }>): string {
  if (v.unit === 'ratio') return `${Math.round(v.value * 100)}% of the time`;
  if (v.unit === 'per_minute') return `${v.value.toFixed(1)} per minute`;
  return `${Math.round(v.value)} s`;
}

/**
 * Observable video evidence (P5). Each value shows its evidence class. An
 * unavailable value is text only — no number, no bar — because nothing was
 * measured.
 */
export function VideoEvidenceSection({ values }: { values: readonly VideoEvidenceValue[] }) {
  return (
    <section data-testid="video-evidence">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">On camera</h3>
      <p className="text-xs text-gray-500 mb-3">
        Observable behaviour only. These are reported beside the assessment and never change its score.
      </p>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {values.map((v) => (
          <li key={v.key} className="flex items-start justify-between gap-4 px-3 py-2" data-state={v.state}>
            <span className="text-sm text-gray-700">{v.label}</span>
            <span className="text-right">
              <span className="block text-xs text-gray-700">
                {v.state === 'available' ? formatVideoValue(v) : (v.reason === 'awaiting_levelup_media' ? VIDEO_UNAVAILABLE_LABEL : v.explanation)}
              </span>
              <span className="block text-[11px] uppercase tracking-wide text-gray-400">
                {EVIDENCE_CLASS_LABEL[v.evidenceClass]}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ConversationResultsProps {
  result: ConversationResult;
  /** Optional: jump to the turn a piece of evidence came from. */
  onViewEvidence?: (turnId: string) => void;
}

export function ConversationResults({ result, onViewEvidence }: ConversationResultsProps) {
  if (result.status !== 'evaluated') {
    return (
      <div className="p-6 text-center text-gray-600">
        <p className="text-sm">This conversation has not been evaluated yet.</p>
      </div>
    );
  }

  const scored = result.scored ?? [];
  const notMeasured = result.notMeasured ?? [];
  // The rubric's own labels first (the API sends them), then product labels.
  const rubricLabels = new Map((result.rubric?.dimensions ?? []).map((d) => [d.key, d.label]));
  const label = (key: string) => rubricLabels.get(key) ?? fallbackLabel(key);
  const interviewPractice = result.rubric?.scope === 'interview_practice';

  return (
    <div className="space-y-8">
      {interviewPractice && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4" role="note">
          <p className="text-sm text-blue-900">{INTERVIEW_PRACTICE_NOTICE}</p>
        </div>
      )}
      {/* Overall — only when the server actually produced one. */}
      <section>
        {result.overallAvailable && typeof result.overall === 'number' ? (
          <div className="text-center">
            <div className="text-4xl font-semibold text-gray-900">{Math.round(result.overall)}</div>
            <div className="text-sm text-gray-500 mt-1">Overall</div>
          </div>
        ) : (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
            <p className="text-sm text-gray-700">
              Individual dimensions were evaluated below. An overall score is not
              available for this assessment
              {result.rubric?.overallUnavailableReason === 'insufficient_coverage' && result.rubric.minScoredForOverall
                ? `: fewer than ${result.rubric.minScoredForOverall} dimensions could be judged from this conversation.`
                : '.'}
            </p>
          </div>
        )}
        {(result.rubric || result.language) && (
          <p className="mt-3 text-xs text-gray-500 text-center" data-testid="result-provenance">
            {result.language && <>Conversation in {result.language.label}. </>}
            {result.rubric && <>Assessed against {result.rubric.title} (v{result.rubric.version}).</>}
            {result.rubric?.scope === 'general_communication' &&
              " This judges general communication skills, not whether the scenario's goal was met."}
            {result.rubricBasis === 'general' && (
              <span data-testid="general-rubric-note">
                {' '}Judged on the general communication rubric: no rubric was authored for this conversation.
              </span>
            )}
            {result.rubric?.scope === 'language' &&
              ' This judges your use of the language from what you said, not pronunciation or accent.'}
          </p>
        )}
      </section>

      {/* Measured dimensions */}
      {scored.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">What was measured</h3>
          <ul className="space-y-4">
            {scored.map((d) => (
              <li key={d.key}>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-gray-800">{label(d.key)}</span>
                  <span className="text-sm tabular-nums text-gray-900">{d.value}</span>
                </div>
                <div className="mt-1 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full', scoreTone(d.value))}
                    style={{ width: `${Math.max(0, Math.min(100, d.value))}%` }}
                  />
                </div>
                {d.evidence.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {d.evidence.map((e, i) =>
                      e.turnId ? (
                        <button
                          key={`${e.turnId}-${i}`}
                          type="button"
                          onClick={() => e.turnId && onViewEvidence?.(e.turnId)}
                          className="text-xs text-primary-700 underline hover:text-primary-900"
                        >
                          See response
                        </button>
                      ) : null
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Not measured. Deliberately a LIST, not bars — a bar at any width implies a
        measurement, and there isn't one.
      */}
      {notMeasured.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Not measured</h3>
          <p className="text-xs text-gray-500 mb-3">
            These need evidence this conversation did not contain. They are not scores of zero.
          </p>
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {notMeasured.map((d) => (
              <li key={d.key} className="flex items-start justify-between gap-4 px-3 py-2">
                <span className="text-sm text-gray-700">{label(d.key)}</span>
                <span className="text-xs text-gray-500 text-right max-w-[60%]">
                  {d.explanation}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.videoEvidence && result.videoEvidence.length > 0 && (
        <VideoEvidenceSection values={result.videoEvidence} />
      )}

      {result.coverage && (
        <p className="text-xs text-gray-500">
          {result.coverage.scoredCount} measured · {result.coverage.notMeasuredCount} not measured
        </p>
      )}
    </div>
  );
}

export default ConversationResults;
